/* CourtageDataRoom — Data Room par mandat.
 * Documents du dossier (teaser public / confidentiel NDA), partage sécurisé,
 * NCNDA du mandat (généré IA) + liste des signatures.
 */
import { useEffect, useRef, useState } from "react";
import { api, apiUpload } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Upload, Loader2, Lock, Unlock, Link2, Download, Trash2, FileText,
  ShieldCheck, Wand2, ChevronDown, ChevronUp, Eye,
} from "lucide-react";
import ShareDocModal from "@/components/ShareDocModal";

function fmtSize(n) {
  if (n == null) return "—";
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

export default function CourtageDataRoom({ mandate }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [shareDoc, setShareDoc] = useState(null);
  const [nda, setNda] = useState(null);
  const [ndaBusy, setNdaBusy] = useState(false);
  const [ndaOpen, setNdaOpen] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, nRes] = await Promise.all([
        api.get("/workspace/documents", { params: { linked_module: "courtage", linked_entity_id: mandate.id } }),
        api.get(`/workspace/courtage/mandates/${mandate.id}/nda`),
      ]);
      setDocs(dRes.data?.items || []);
      setNda(nRes.data || null);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mandate.id]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("module_source", "courtage");
      fd.append("linked_module", "courtage");
      fd.append("linked_entity_id", mandate.id);
      try {
        const r = await apiUpload("/workspace/documents", fd);
        // Data Room : confidentiel par défaut
        await api.patch(`/workspace/documents/${r.data.id}/confidential`, { confidential: true });
        notify.success(`« ${f.name} » ajouté à la Data Room.`);
      } catch (e) { notify.error(fmtError(e, `Échec : ${f.name}`)); }
    }
    setUploading(false);
    await load();
  };

  const toggleConfidential = async (d) => {
    try {
      await api.patch(`/workspace/documents/${d.id}/confidential`, { confidential: !d.confidential });
      setDocs((prev) => prev.map((x) => (x.id === d.id ? { ...x, confidential: !d.confidential } : x)));
    } catch (e) { notify.error(fmtError(e)); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer « ${d.name} » de la Data Room ?`)) return;
    try { await api.delete(`/workspace/documents/${d.id}`); notify.success("Supprimé."); await load(); }
    catch (e) { notify.error(fmtError(e)); }
  };

  const generateNda = async () => {
    setNdaBusy(true);
    try {
      const r = await api.post(`/workspace/courtage/mandates/${mandate.id}/nda/generate`);
      setNda((n) => ({ ...(n || {}), template: r.data.template }));
      setNdaOpen(true);
      notify.success("NCNDA personnalisé généré par l'IA.");
    } catch (e) { notify.error(fmtError(e)); }
    setNdaBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="dataroom-root">
      {/* NCNDA */}
      <div className="noam-card p-4" data-testid="dataroom-nda-card">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#1D9E75]" />
            <div>
              <div className="text-sm font-medium text-zinc-100">NCNDA du mandat</div>
              <div className="text-[11px] text-zinc-500">
                {nda?.template?.source === "ai"
                  ? `Personnalisé par l'IA le ${(nda.template.generated_at || "").slice(0, 10)}`
                  : "Modèle standard suisse (confidentialité + non-contournement)"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={generateNda} disabled={ndaBusy} className="btn-secondary text-xs disabled:opacity-50" data-testid="dataroom-nda-generate">
              {ndaBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              {nda?.template?.source === "ai" ? "Régénérer (IA)" : "Personnaliser (IA)"}
            </button>
            <button onClick={() => setNdaOpen((o) => !o)} className="btn-secondary text-xs" data-testid="dataroom-nda-toggle">
              {ndaOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} Texte
            </button>
          </div>
        </div>
        {ndaOpen && nda?.template?.content && (
          <div className="mt-3 bg-black/30 border border-white/10 rounded p-3 max-h-64 overflow-y-auto whitespace-pre-line text-[11px] leading-relaxed text-zinc-300" data-testid="dataroom-nda-text">
            {nda.template.content}
          </div>
        )}
        {(nda?.signed || []).length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">NDA signés ({nda.signed.length})</div>
            <div className="space-y-1">
              {nda.signed.map((s) => (
                <div key={s.id} className="text-[11px] text-zinc-300 flex items-center gap-2 flex-wrap" data-testid={`dataroom-nda-signed-${s.id}`}>
                  <ShieldCheck className="w-3 h-3 text-[#1D9E75]" />
                  <span className="font-medium">{s.signer_name}</span>
                  {s.signer_company && <span className="text-zinc-500">({s.signer_company})</span>}
                  <span className="text-zinc-500">{s.signer_email}</span>
                  <span className="text-zinc-600">· {(s.signed_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="noam-card p-4" data-testid="dataroom-docs-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-zinc-100">Documents du dossier</div>
          <button onClick={() => fileInput.current?.click()} disabled={uploading} className="btn-primary text-xs disabled:opacity-50" data-testid="dataroom-upload-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Ajouter
          </button>
          <input ref={fileInput} type="file" multiple className="hidden"
            onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="dataroom-file-input" />
        </div>
        {loading ? (
          <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</div>
        ) : docs.length === 0 ? (
          <div className="text-xs text-zinc-600 text-center py-6">
            Aucun document. Ajoutez les pièces du dossier (plans, états locatifs, contrats…). Elles seront
            confidentielles par défaut — partageables via lien sécurisé avec NDA.
          </div>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-2 bg-black/20 border border-white/5 rounded p-2" data-testid={`dataroom-doc-${d.id}`}>
                <FileText className="w-4 h-4 text-[#378ADD] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-zinc-100 truncate">{d.name}</div>
                  <div className="text-[10px] text-zinc-500">{fmtSize(d.size_bytes)} · {(d.uploaded_at || "").slice(0, 10)}</div>
                </div>
                <button onClick={() => toggleConfidential(d)}
                  className={`text-[10px] px-2 py-1 rounded border flex items-center gap-1 shrink-0 ${d.confidential ? "border-[#EF9F27]/40 text-[#EF9F27] bg-[#EF9F27]/10" : "border-white/10 text-zinc-400"}`}
                  title={d.confidential ? "Confidentiel — NDA conseillé pour le partage" : "Public (ex: teaser)"}
                  data-testid={`dataroom-conf-${d.id}`}>
                  {d.confidential ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                  {d.confidential ? "Confidentiel" : "Public"}
                </button>
                <button onClick={() => setShareDoc(d)} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#378ADD]" title="Partager" data-testid={`dataroom-share-${d.id}`}>
                  <Link2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => window.open(`${api.defaults.baseURL}/workspace/documents/${d.id}/preview`, "_blank")}
                  className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white" title="Aperçu">
                  <Eye className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => window.open(`${api.defaults.baseURL}/workspace/documents/${d.id}/download`, "_blank")}
                  className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white" title="Télécharger">
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(d)} className="p-1.5 rounded hover:bg-white/5 text-zinc-500 hover:text-red-400" title="Supprimer">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {shareDoc && (
        <ShareDocModal doc={shareDoc} mandateId={mandate.id} onClose={() => { setShareDoc(null); load(); }} />
      )}
    </div>
  );
}
